import { useMutation } from "@apollo/client";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  CreateSaleInput,
  Sale as SaleAPIType,
  UpdateSaleInput,
} from "@towersystems/roam-common/lib/generated-types";
import { useEffect, useState } from "react";
import { SyncState } from "../../../../utilities/data/constants";
import { useSyncableEntities } from "../../../../utilities/data/hooks";
import Sale from "../../../../utilities/data/model/Sale";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { SyncSaleContext } from "../../../../utilities/sale-sync";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { createSale, updateSale } from "../../utils/sale/constants";
import React from "react";

export interface SaleSyncControllerProps {
  children?: React.ReactNode;
}

// this bootleg queue is a temporary solution
export const SaleSyncController = ({ children }: SaleSyncControllerProps) => {
  const runningRef = React.useRef(false);

  const [queue, setQueue] = useState<string[]>([]);
  const [createSaleMutation] = useMutation<SaleAPIType>(createSale);
  const [updateSaleMutation] = useMutation<SaleAPIType>(updateSale);
  const { findSyncableEntities } = useSyncableEntities();
  const online = useOnline();

  const settings = useLocalSettingsProvider();
  const { salesQueueEnabled } = settings.settings;

  const canSync = online && salesQueueEnabled;
  const database = useDatabase();

  const getAllSalesWithStatusUPLOAD = async () => {
    const sales = await findSyncableEntities<Sale>("sales");
    return sales;
  };

  const enqueSyncableSales = async () => {
    const s = (await getAllSalesWithStatusUPLOAD()).map((s) => s.id);
    setQueue((q) => [...new Set([...q, ...s])]);
  };

  const removeItemFromQueue = async (saleId: string) => {
    setQueue((q) => q.filter((i) => i !== saleId));
    await database.write(async () => {
      const sale = await database.collections.get<Sale>("sales").find(saleId);
      sale.destroyPermanently();
    });
  };

  useEffect(() => {
    if (queue.length >= 1) {
      processQ();
    }
  }, [queue, canSync]);

  const running = runningRef.current;

  const setRunning = (value: boolean) => {
    runningRef.current = value;
  };

  useEffect(() => {
    if (canSync) {
      enqueSyncableSales();
    }
  }, [canSync]);

  const processQ = async () => {
    if (canSync && running === false) {
      if (queue.length >= 1) {
        setRunning(true);
        for (const saleId of queue) {
          try {
            await processAndSyncSaleById(saleId);
          } catch (e) {}
        }
        setRunning(false);
      }
    }
  };

  const addSaleToQueue = async (saleId: string): Promise<boolean> => {
    try {
      const sale = await database.collections.get<Sale>("sales").find(saleId);
      if (sale.syncState === SyncState.UPLOAD) {
        setQueue((q) => [...new Set([...q, saleId])]);
      }
      return true;
    } catch (e) {
      console.log(e);
    }
    return false;
  };

  const processAndSyncSaleById = async (saleId: string) => {
    if (online) {
      const sale = await database.collections.get<Sale>("sales").find(saleId);

      await processAndSyncSale(sale);
    }
  };

  const formatSale = async (
    sale: Sale
  ): Promise<CreateSaleInput | UpdateSaleInput> => {
    let input: any;

    input = {
      state: sale.saleState,
      purchaseOrderNumber: sale.purchaseOrderNumber || null,
      customerId: sale.customer.id ? sale.customer.id : null,
      note: sale.note || null,
      receiptTo: sale.receiptTo || null,
      quoteAccepted: sale.quoteAccepted ?? false,
      giftVoucherCode: sale.giftVoucherCode || null,
      expectedPickupDate: sale.expectedPickupDate
        ? sale.expectedPickupDate.toISOString()
        : null,
      lineItems: (await sale.items.fetch()).map((i) => {
        return {
          id: i.externalId,
          productId: i.product.id,
          productKitId: i.productKit?.id || null,
          isKit: !!i.productKit.id,
          quantity: i.quantity,
          price: i.price - i.tax,
          basePrice: i.basePrice,
          basePriceInclTax: i.basePrice,
          stockCode: i.stockCode,
          stockNumber: i.stockNumber,
          priceInclTax: i.price,
          tax: i.tax,
          cost: i.cost,
          taxRatePercent: i.taxRatePercent,
          loyaltyPointEarned: i.loyaltyPointEarned,
          serialNumber: i.serialNumber,
          name: i.name,
          giftCardNumber: i.giftCardNumber,
          adjustments: i.adjustments.map((a) => ({
            id: a.id,
            type: a.type,
            amount: a.amount,
            unitLoyaltyPointsUsed: a.unitLoyaltyPointsUsed,
          })),
          metadata: i.metadata,
          customerNote: i.customerNote,
        };
      }),
      payments:
        sale.payments?.map((p) => ({
          id: p.id,
          paymentMethodId: p.paymentMethodId,
          transactionId: p.transactionId,
          paymentDate: p.paymentDate,
          amount: p.amount,
          metadata: p.metadata,
          state: p.state,
        })) || [],
    };

    if (sale.externalId) {
      input.id = sale.externalId;
    } else {
      input.invoiceNumber = sale.invoiceNumber;
      input.invoiceSequence = sale.invoiceSequence;
      input.locationId = sale.location.id;
      input.terminalId = sale.terminal.id;
      input.staffId =
        sale.staff.id && sale.staff.id.length >= 1 ? sale.staff.id : undefined;
    }

    return input;
  };

  const processAndSyncSale = async (sale: Sale) => {
    let time = new Date();
    const formattedSaleData = await formatSale(sale);

    try {
      if (sale.syncState !== SyncState.UPLOAD) {
        return;
      }

      //@ts-ignore
      if (formattedSaleData.id) {
        const result = await updateSaleMutation({
          variables: { input: formattedSaleData },
        });

        database.write(async () => {
          await sale.update((s) => {
            //@ts-ignore
            s.externalId = result.data.updateSale.id;
            s.syncState = SyncState.UPLOADED;
          });
        });
      } else {
        const result = await createSaleMutation({
          variables: { input: formattedSaleData },
        });

        database.write(async () => {
          await sale.update((s) => {
            //@ts-ignore
            s.externalId = result.data.createSale.id;
            s.syncState = SyncState.UPLOADED;
          });
        });
      }

      setQueue((q) => {
        return q.filter((id) => id !== sale.id);
      });
    } catch (e) {
      console.log(e);
    }

    await sale.increaseSyncAttempts(time);
  };

  return (
    <SyncSaleContext.Provider
      value={{
        formatSale,
        processAndSyncSaleById,
        removeItemFromQueue,
        addSaleToQueue,

        running,
      }}
    >
      {children}
    </SyncSaleContext.Provider>
  );
};
